import { css, cx } from "@emotion/css";
import { UnreachableCaseError } from "libs/errors";
import { isEqual } from "libs/predicates";
import { blueA } from "@radix-ui/colors";
import dayjs from "dayjs";
import { format, formatDistanceToNow } from "date-fns";
import { useObservable, useObservableEagerState, useObservableState } from "observable-hooks";
import { CSSProperties, memo, ReactNode } from "react";
import { BsFillStarFill, BsLockFill } from "react-icons/bs";
import { distinctUntilChanged, map, switchMap, interval } from "rxjs";
import { WINDOW_SIZE$ } from "~/utils/dom-helpers";
import { Tooltip } from "../Tooltip";
import { ReactElement } from "rehype-react/lib";
import { ShortcutHint, ShortcutHintContents, hint } from "~/environment/hint-service";
import { MdDone, MdOutlineDelete, MdRemoveDone, MdRssFeed, MdSchedule } from "react-icons/md";
import {
  deleteDraftCommand,
  markDoneCommand,
  markNotDoneCommand,
  openCommandBarCommand,
  setThreadReminderCommand,
  subscribeToTagCommand,
  unsubscribeFromTagCommand,
} from "~/utils/common-commands";
import { GoCommandPalette } from "react-icons/go";
import { ParentComponent } from "~/utils/type-helpers";
import { startWith } from "libs/rxjs-operators";
import { MS_IN_MINUTE } from "libs/date-helpers";

/* -------------------------------------------------------------------------------------------------
 * entryCSSClasses
 * -----------------------------------------------------------------------------------------------*/

// We need to use CSS classes combined with an `<li>`
// element (rather than a react component) because the
// `<Slot>` component doesn't appear to support providing
// a React component as a child (not even a forwardRef).
export const entryCSSClasses = cx(
  `
    flex items-center 
    py-2 px-4 sm-w:px-8 md-w:px-12
    hover:cursor-pointer border-l-2 
    border-white outline-none focus:bg-slate-4 
    focus:border-black group
  `,
  css`
    &.is-checked {
      background-color: ${blueA.blueA7};
      border-color: ${blueA.blueA7};
    }

    @media (hover: hover) and (pointer: fine) {
      &.is-checked:focus {
        background-color: ${blueA.blueA7};
        border-color: black;
      }
    }
  `,
);

/* -------------------------------------------------------------------------------------------------
 * Recipients
 * -----------------------------------------------------------------------------------------------*/

export const Recipients: ParentComponent<{
  style?: CSSProperties;
  className?: string;
  nonTruncatedPrefix?: ReactNode;
  nonTruncatedSuffix?: ReactNode;
}> = (props) => {
  return (
    <div
      className={cx("EntryRecipients flex items-center overflow-hidden pr-4 md-w:w-[188px]", props.className)}
      style={{ ...props.style }}
    >
      {props.nonTruncatedPrefix}
      {props.children && <div className="truncate">{props.children}</div>}
      {props.nonTruncatedSuffix}
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Summary
 * -----------------------------------------------------------------------------------------------*/

export const Summary: ParentComponent<{
  subject?: string | ReactElement | null;
  formatAsReply?: boolean;
  details?: string | ReactElement | null;
}> = (props) => {
  // These styles were built after inspecting the styles that Gmail uses
  // to display inbox entries. The combination is necessary to support
  // first truncating the body and then truncating the subject if
  // necessary.
  return (
    <div className="flex items-center flex-[3_3_0%] min-w-0 mr-3">
      {props.subject && (
        <span className="EntrySummary-subject shrink truncate mr-4 min-w-0">
          {props.formatAsReply ? "Re: " : ""}
          {props.subject}
        </span>
      )}

      <span className="EntrySummary-details text-slateA-9 truncate flex-1 min-w-0">{props.details}</span>
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * PrivateEntryIcon
 * -----------------------------------------------------------------------------------------------*/

export const PrivateEntryIcon: ParentComponent<{}> = () => {
  return (
    <span className="inline-flex shrink-0 ml-2">
      <small>
        <BsLockFill />
      </small>
    </span>
  );
};

/* -------------------------------------------------------------------------------------------------
 * StarredEntryIcon
 * -----------------------------------------------------------------------------------------------*/

export const StarredEntryIcon: ParentComponent<{}> = () => {
  return (
    <span className="inline-flex shrink-0 ml-2">
      <small>
        <BsFillStarFill className="text-yellow-10 stroke-yellow-11 stroke-[0.5]" />
      </small>
    </span>
  );
};

/* -------------------------------------------------------------------------------------------------
 * EntryTimestamp
 * -----------------------------------------------------------------------------------------------*/

export const EntryTimestamp: ParentComponent<{
  datetime: string;
  /** default is "sm" */
  size?: "sm" | "md" | "lg";
  alwaysShowTime?: boolean;
}> = (props) => {
  return (
    <div className="flex items-baseline text-sm text-nowrap ml-4">
      <span className="text-slateA-9 uppercase">
        <DisplayDate date={props.datetime} size={props.size} alwaysShowTime={props.alwaysShowTime} />
      </span>
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * DisplayDate
 * -----------------------------------------------------------------------------------------------*/

export const DisplayDate: ParentComponent<{
  date: string | dayjs.Dayjs;
  /** default is "sm" */
  size?: "sm" | "md" | "lg" | "relative";
  alwaysShowTime?: boolean;
  className?: string;
}> = memo((props) => {
  const date = useFormatDate(props);

  if (date.display === "never") {
    return (
      <Tooltip side="left" content={date.tooltip} delayDuration={500}>
        <span className={props.className}>{date.display}</span>
      </Tooltip>
    );
  }

  return (
    <Tooltip side="left" content={date.tooltip} delayDuration={500}>
      <time dateTime={date.iso} className={props.className}>
        {date.display}
      </time>
    </Tooltip>
  );
}, isEqual);

/* -----------------------------------------------------------------------------------------------*/

function useFormatDate(props: {
  date: string | dayjs.Dayjs;
  /** default is "sm" */
  size?: "sm" | "md" | "lg" | "relative";
  alwaysShowTime?: boolean;
}) {
  const observable = useObservable(
    (input$) =>
      input$.pipe(
        switchMap(([inputDate, size, alwaysShowTime]) =>
          interval(MS_IN_MINUTE).pipe(
            startWith(() => null),
            map(() => formatDate(inputDate, size, alwaysShowTime)),
          ),
        ),
        distinctUntilChanged(isEqual),
      ),
    [props.date, props.size, props.alwaysShowTime],
  );

  return useObservableState(observable, () => formatDate(props.date, props.size, props.alwaysShowTime));
}

/* -----------------------------------------------------------------------------------------------*/

function formatDate(
  inputDate: string | dayjs.Dayjs,
  size: "sm" | "md" | "lg" | "relative" | undefined,
  alwaysShowTime: boolean | undefined,
) {
  const date = dayjs(inputDate);
  const now = dayjs();

  if (date.diff(now, "years") > 50) {
    return {
      iso: date.toISOString(),
      tooltip: "This date will never arrive",
      display: "never",
    };
  }

  const formatStr = formatStrForDisplayDate(date, size, alwaysShowTime);

  return {
    iso: date.toISOString(),
    tooltip: date.format("LLLL"),
    display: format(date.toDate(), formatStr),
  };
}

/* -----------------------------------------------------------------------------------------------*/

/**
 * @returns a formatting string suitable for passing to `date-fns#format()`
 */
function formatStrForDisplayDate(
  date: dayjs.Dayjs,
  size: "sm" | "md" | "lg" | "relative" = "sm",
  alwaysShowTime = false,
) {
  let formatStr: string;
  const now = dayjs();

  switch (size) {
    case "lg": {
      if (date.isSame(now, "date")) {
        return "'today at' h:mm aaa";
      }

      formatStr = date.isSame(now, "year") ? "EEEE, MMMM d" : "EEEE, MMMM d, yyyy";

      break;
    }
    case "md": {
      if (date.isSame(now, "date")) {
        return "h:mm aaa";
      }

      formatStr = date.isSame(now, "year") ? "EEE, MMM d" : "EEE, M/d/yy";

      break;
    }
    case "sm": {
      if (date.isSame(now, "date")) {
        return "h:mm aaa";
      }

      formatStr = date.isSame(now, "year") ? "MMM d" : "M/d/yy";

      break;
    }
    case "relative": {
      return `'${formatDistanceToNow(date.toDate())}'`;
    }
    default: {
      throw new UnreachableCaseError(size);
    }
  }

  if (alwaysShowTime) {
    formatStr += ", h:mm aaa";
  }

  return formatStr;
}

/* -------------------------------------------------------------------------------------------------
 * useShouldShowChannelLabels
 * -----------------------------------------------------------------------------------------------*/

export function useShouldShowChannelLabels() {
  return useObservableEagerState(SHOW_CHANNEL_LABELS$);
}

const SHOW_CHANNEL_LABELS$ = WINDOW_SIZE$.pipe(
  map(({ width }) => width >= 800),
  distinctUntilChanged(),
);

/* -------------------------------------------------------------------------------------------------
 * EntryActions
 * -----------------------------------------------------------------------------------------------*/

export const EntryActions: ParentComponent<{
  defaultComponent?: ReactNode;
  alwaysShowActions?: boolean;
}> = (props) => {
  return (
    <>
      {props.defaultComponent && <div className={entryActionsDefaultComponentCss}>{props.defaultComponent}</div>}

      <div className={cx(props.alwaysShowActions ? "flex" : entryActionsCss, "flex-row items-end ml-8 space-x-3")}>
        {props.children}
      </div>
    </>
  );
};

const entryActionsDefaultComponentCss = css`
  @media (hover: hover) and (pointer: fine) {
    [role="list"]:not(.has-selected-entries) [role="listitem"]:focus & {
      display: none;
    }
  }
`;

const entryActionsCss = css`
  display: none;

  @media (hover: hover) and (pointer: fine) {
    [role="list"]:not(.has-selected-entries) [role="listitem"]:focus & {
      display: flex;
    }
  }
`;

/* -------------------------------------------------------------------------------------------------
 * EntryAction
 * -----------------------------------------------------------------------------------------------*/

export const EntryAction: ParentComponent<{
  tooltip: string | ReactElement;
  onClick: () => void;
  icon: ReactNode;
}> = (props) => {
  return (
    <Tooltip side="bottom" content={props.tooltip}>
      <button
        className="hover:bg-slateA-3 text-black p-1 rounded-full border border-black"
        onClick={(e) => {
          e.preventDefault();
          props.onClick();
        }}
      >
        {props.icon}
      </button>
    </Tooltip>
  );
};

/* -------------------------------------------------------------------------------------------------
 * MarkDoneEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const MarkDoneEntryAction: ParentComponent<{}> = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Mark done
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`E`} />
          </span>
        </p>
      }
      icon={<MdDone />}
      onClick={() => {
        markDoneCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`E`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * MarkNotDoneEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const MarkNotDoneEntryAction: ParentComponent<{}> = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Mark not done
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`Shift+E`} />
          </span>
        </p>
      }
      icon={<MdRemoveDone />}
      onClick={() => {
        markNotDoneCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`Shift+E`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * SetReminderEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const SetReminderEntryAction: ParentComponent<{}> = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Set reminder
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`H`} />
          </span>
        </p>
      }
      icon={<MdSchedule />}
      onClick={() => {
        setThreadReminderCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`H`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * OtherCommandEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const OtherCommandEntryAction: ParentComponent<{}> = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Other command...
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`$mod+K`} />
          </span>
        </p>
      }
      icon={<GoCommandPalette />}
      onClick={() => {
        openCommandBarCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`$mod+K`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * DeleteDraftEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const DeleteDraftEntryAction: ParentComponent<{}> = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Delete draft
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`$mod+Shift+,`} />
          </span>
        </p>
      }
      icon={<MdOutlineDelete />}
      onClick={() => {
        deleteDraftCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`$mod+Shift+,`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * SubscribeToTagEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const SubscribeToTagEntryAction: ParentComponent = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Subscribe
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`S+S`} />
          </span>
        </p>
      }
      icon={<MdRssFeed />}
      onClick={() => {
        subscribeToTagCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`S+S`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * UnsubscribeToTagEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const UnsubscribeToTagEntryAction: ParentComponent = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Unsubscribe
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`S+U`} />
          </span>
        </p>
      }
      icon={<MdRssFeed />}
      onClick={() => {
        unsubscribeFromTagCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`S+U`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * MarkAllDoneEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const MarkAllDoneEntryAction: ParentComponent = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Mark all done
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`E`} />
          </span>
        </p>
      }
      icon={<MdDone />}
      onClick={() => {
        markDoneCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`E`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * MarkAllNotDoneEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const MarkAllNotDoneEntryAction: ParentComponent = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Mark all not done
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`Shift+E`} />
          </span>
        </p>
      }
      icon={<MdRemoveDone />}
      onClick={() => {
        markNotDoneCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`Shift+E`} />,
        });
      }}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * SetReminderForAllEntryAction
 * -----------------------------------------------------------------------------------------------*/

export const SetReminderForAllEntryAction: ParentComponent = () => {
  return (
    <EntryAction
      tooltip={
        <p className="inline-flex items-center">
          Set reminder for all
          <span className="inline-flex items-center ml-2">
            <ShortcutHintContents hint={`H`} />
          </span>
        </p>
      }
      icon={<MdSchedule />}
      onClick={() => {
        setThreadReminderCommand.trigger();
        hint("quiet", {
          content: <ShortcutHint hint={`H`} />,
        });
      }}
    />
  );
};

/* -----------------------------------------------------------------------------------------------*/
